import React, { useRef, useEffect } from 'react';
import { DrawerContext } from './DrawerContext';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useScrollingHandler } from './hooks/useScrollingHandler';
import { DrawerProps } from './types';

const Drawer = ({
  autoOpen = false,
  isOpen: isOpenControllable,
  preserveDrawerScrollLocation = false,
  onClose: onControllableClose = () => undefined,
  onOpen: onControllableOpen = () => undefined,
  children,
}: DrawerProps) => {
  const [innerIsOpen, setInnerIsOpen] = React.useState(autoOpen);

  const isOpen = isOpenControllable ?? innerIsOpen;

  const contentRef = useRef<HTMLDivElement>();
  const drawerRef = useRef<HTMLElement>(null);

  const hasOpened = useRef(false);

  const onClose = React.useCallback(() => {
    setInnerIsOpen(false);
    onControllableClose();
  }, [onControllableClose]);

  const onOpen = React.useCallback(() => {
    setInnerIsOpen(true);
    onControllableOpen();
  }, [onControllableOpen]);

  if (autoOpen && isOpenControllable) {
    throw new Error(
      "Drawer: Don't use both autoOpen and isOpen together, autoOpen is only meant to be used with the trigger component"
    );
  }

  useEffect(() => {
    if (drawerRef.current && isOpen && !hasOpened.current) {
      hasOpened.current = true;
      drawerRef.current.focus();
    }
  }, [isOpen]);

  const { close } = useFocusTrap({
    isOpen,
    element: drawerRef.current,
    onClose,
  });

  const { isContentScrollable } = useScrollingHandler({
    isOpen,
    contentRef,
    preserveDrawerScrollLocation,
  });

  return (
    <DrawerContext.Provider value={{ isOpen, open: onOpen, close, contentRef, isContentScrollable }}>
      {children}
    </DrawerContext.Provider>
  );
};

Drawer.displayName = 'Drawer';

export default Drawer;
